import { ArrowLeft, ChevronDown, ShieldPlus, Sparkles, UserPlus } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { bytes, createVault, sealSecretTo } from "@tether/crypto";
import { useSession } from "../App.js";
import { api, type VaultMode } from "../lib/api.js";
import { getOrCreateGroupKey } from "../lib/keys.js";
import { MODE_DESCRIPTIONS, MODE_ICONS, MODE_LABELS, ModePicker } from "../lib/vaultModes.js";

interface Invite {
  name: string;
  inviteUrl: string;
  code: string;
  expiresAt: string;
}

export function NewVault() {
  const { me, refresh } = useSession();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const modeParam = params.get("mode") as VaultMode | null;
  const mode: VaultMode | null =
    modeParam === "kid" || modeParam === "guardian" || modeParam === "circle" ? modeParam : null;

  const [name, setName] = useState("");
  const [k, setK] = useState(3);
  const [n, setN] = useState(5);
  const [windowMinutes, setWindowMinutes] = useState(15);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Once the vault exists, guardian/circle modes fold the first invite into the same flow instead
  // of a separate trip to the vault page to find the invite button.
  const [createdVaultId, setCreatedVaultId] = useState<string | null>(null);
  const [inviteName, setInviteName] = useState("");
  const [invite, setInvite] = useState<Invite | null>(null);

  async function create() {
    if (!me || !mode) return;
    setBusy(true);
    setErr(null);
    try {
      const ownerKey = getOrCreateGroupKey(me.user.id);
      if (!me.user.groupPublicKey)
        await api("/v1/me/group-key", {
          body: { groupPublicKey: bytes.toBase64Url(ownerKey.publicKey) },
        });
      const effectiveK = mode === "circle" ? k : 1;
      const effectiveN = mode === "circle" ? n : 1;
      const vault = createVault(effectiveK, effectiveN);
      const vpk = bytes.toBase64Url(vault.publicKey);
      const sealedMetadataSecretForOwner = bytes.toBase64Url(
        sealSecretTo(ownerKey.publicKey, vault.metadataSecretKey, "msk"),
      );
      const body: Record<string, unknown> = {
        name,
        mode,
        publicKey: vpk,
        metadataPublicKey: bytes.toBase64Url(vault.metadataPublicKey),
        k: effectiveK,
        n: effectiveN,
        windowMinutes,
        sealedMetadataSecretForOwner,
      };
      if (mode === "kid") {
        body["sealedShareForOwner"] = bytes.toBase64Url(
          sealSecretTo(ownerKey.publicKey, vault.shares[0]!.value, `share:${vpk}:1`),
        );
        body["pendingShares"] = [];
      } else {
        const { claimPublicKey } = await api<{ claimPublicKey: string }>("/v1/setup/claim-key");
        const claimPk = bytes.fromBase64Url(claimPublicKey);
        body["pendingShares"] = vault.shares.map((s) => ({
          index: s.index,
          sealed: bytes.toBase64Url(sealSecretTo(claimPk, s.value, `share:${vpk}:${s.index}`)),
        }));
      }
      for (const s of vault.shares) s.value.fill(0);
      vault.metadataSecretKey.fill(0);
      const r = await api<{ vault: { id: string } }>("/v1/vaults", { body });
      await refresh();
      if (mode === "kid") {
        nav(`/vaults/${r.vault.id}`, { replace: true });
      } else {
        setCreatedVaultId(r.vault.id);
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function invite1() {
    if (!createdVaultId) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ inviteUrl: string; code: string; expiresAt: string }>(
        `/v1/vaults/${createdVaultId}/confidants`,
        { body: { name: inviteName } },
      );
      setInvite({ name: inviteName, ...r });
      setInviteName("");
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!mode) return <ModePicker onPick={(m) => setParams({ mode: m }, { replace: true })} />;

  if (createdVaultId) {
    const label = mode === "guardian" ? "co-owner" : "confidant";
    return (
      <div className="card" style={{ maxWidth: 620 }}>
        <h2 style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 8 }}>
          <Sparkles size={20} className="icon" />
          Invite your {label}
        </h2>
        <p className="muted">
          {name} is created. Invite {mode === "guardian" ? "your co-owner" : "your first confidant"}{" "}
          now, or come back to this later from the vault page.
        </p>
        {invite ? (
          <div className="card" style={{ marginTop: 12 }}>
            <h3 style={{ marginTop: 0 }}>Invitation for {invite.name}</h3>
            <p>
              Send them this link: <code>{invite.inviteUrl}</code>
            </p>
            <p>
              Tell them this code <strong>in person or by voice</strong>, never in the same message
              as the link:
            </p>
            <div className="code">{invite.code}</div>
            <p className="muted">Expires {new Date(invite.expiresAt).toLocaleString()}.</p>
          </div>
        ) : (
          <div className="row" style={{ marginTop: 12 }}>
            <input
              style={{ maxWidth: 260 }}
              placeholder={`${label === "co-owner" ? "Co-owner's" : "Confidant's"} name`}
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
            />
            <button
              className="primary"
              onClick={invite1}
              disabled={busy || !inviteName}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <UserPlus size={15} />
              Send invite
            </button>
          </div>
        )}
        {err && <p className="error">{err}</p>}
        <div className="row" style={{ marginTop: 16 }}>
          <button className="primary" onClick={() => nav(`/vaults/${createdVaultId}`)}>
            Done
          </button>
        </div>
      </div>
    );
  }

  const nameLabel =
    mode === "kid"
      ? "Your child's name"
      : mode === "guardian"
        ? "Your name"
        : "Who does this vault protect?";
  const namePlaceholder =
    mode === "kid" ? "e.g. Maya" : mode === "guardian" ? "e.g. Sam" : "e.g. Maya";

  return (
    <div className="card" style={{ maxWidth: 620 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
        <div style={{ fontSize: 24 }}>{MODE_ICONS[mode]}</div>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0 }}>New vault · {MODE_LABELS[mode]}</h2>
        </div>
        <button
          onClick={() => setParams({}, { replace: true })}
          className="muted"
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <ArrowLeft size={14} />
          Change mode
        </button>
      </div>
      <p className="muted" style={{ marginTop: 8 }}>
        {MODE_DESCRIPTIONS[mode]}
      </p>
      <label>{nameLabel}</label>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder={namePlaceholder} />

      {mode === "circle" && (
        <div className="grid" style={{ marginTop: 8 }}>
          <div>
            <label>Confidants (n)</label>
            <select value={n} onChange={(e) => setN(Number(e.target.value))}>
              {[3, 4, 5].map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Approvals needed (k)</label>
            <select value={k} onChange={(e) => setK(Number(e.target.value))}>
              {[2, 3, 4, 5]
                .filter((x) => x <= n)
                .map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
            </select>
          </div>
        </div>
      )}
      {mode === "circle" && (
        <p className="muted">
          With {k} of {n}, up to {n - k} confidant{n - k === 1 ? "" : "s"} can lose their phone and
          the vault still opens.
        </p>
      )}

      {!showAdvanced ? (
        <button
          onClick={() => setShowAdvanced(true)}
          className="muted"
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "10px 0",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <ChevronDown size={14} />
          Advanced options
        </button>
      ) : (
        <div style={{ marginTop: 8 }}>
          <label>Unlock granularity</label>
          <select value={windowMinutes} onChange={(e) => setWindowMinutes(Number(e.target.value))}>
            {[5, 10, 15, 30, 60].map((x) => (
              <option key={x} value={x}>
                {x} minutes
              </option>
            ))}
          </select>
        </div>
      )}

      {err && <p className="error">{err}</p>}
      <button
        className="primary"
        style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 6 }}
        onClick={create}
        disabled={busy || !name || (mode === "circle" && k > n)}
      >
        <ShieldPlus size={16} />
        {busy ? "Creating…" : "Create vault"}
      </button>
    </div>
  );
}
